// ========================================
// SERVICE - Blog
// Gestion des articles de blog
// ========================================

import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  increment
} from 'firebase/firestore';
import { db } from './config';

const COLLECTION = 'blogPosts';

/**
 * Récupérer tous les articles
 */
export const getAllPosts = async (status = null) => {
  try {
    let q;
    
    if (status) {
      q = query(
        collection(db, COLLECTION), 
        where('status', '==', status),
        orderBy('publishedAt', 'desc')
      );
    } else {
      q = query(collection(db, COLLECTION), orderBy('createdAt', 'desc'));
    }
    
    const snapshot = await getDocs(q);
    
    const posts = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    return { success: true, data: posts };
  } catch (error) {
    console.error('Erreur getAllPosts:', error);
    return { success: false, error: error.message, data: [] };
  }
};

/**
 * Récupérer un article par son slug
 */
export const getPostBySlug = async (slug) => {
  try {
    const q = query(collection(db, COLLECTION), where('slug', '==', slug));
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      return { success: false, error: 'Article non trouvé' };
    }

    const doc = snapshot.docs[0];
    return { success: true, data: { id: doc.id, ...doc.data() } };
  } catch (error) {
    console.error('Erreur getPostBySlug:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Récupérer les articles par catégorie
 */
export const getPostsByCategory = async (category) => {
  try {
    const q = query(
      collection(db, COLLECTION), 
      where('category', '==', category),
      where('status', '==', 'published'),
      orderBy('publishedAt', 'desc')
    );
    const snapshot = await getDocs(q);
    
    const posts = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    return { success: true, data: posts };
  } catch (error) {
    console.error('Erreur getPostsByCategory:', error);
    return { success: false, error: error.message, data: [] };
  }
};

/**
 * Récupérer les articles récents
 */
export const getRecentPosts = async (count = 3) => {
  try {
    const q = query(
      collection(db, COLLECTION), 
      where('status', '==', 'published'),
      orderBy('publishedAt', 'desc'),
      limit(count)
    );
    const snapshot = await getDocs(q);
    
    const posts = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    return { success: true, data: posts };
  } catch (error) {
    console.error('Erreur getRecentPosts:', error);
    return { success: false, error: error.message, data: [] };
  }
};

/**
 * Ajouter un article
 */
export const addPost = async (postData) => {
  try {
    const now = serverTimestamp();
    
    const docRef = await addDoc(collection(db, COLLECTION), {
      ...postData,
      views: 0,
      likes: 0,
      createdAt: now,
      updatedAt: now,
      publishedAt: postData.status === 'published' ? now : null
    });

    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Erreur addPost:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Mettre à jour un article
 */
export const updatePost = async (postId, postData) => {
  try {
    const docRef = doc(db, COLLECTION, postId);
    
    const updateData = {
      ...postData,
      updatedAt: serverTimestamp()
    };
    
    // Si on publie pour la première fois
    if (postData.status === 'published') {
      const existingDoc = await getDoc(docRef);
      if (existingDoc.exists() && !existingDoc.data().publishedAt) {
        updateData.publishedAt = serverTimestamp();
      }
    }
    
    await updateDoc(docRef, updateData);

    return { success: true };
  } catch (error) {
    console.error('Erreur updatePost:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Supprimer un article
 */
export const deletePost = async (postId) => {
  try {
    await deleteDoc(doc(db, COLLECTION, postId));
    return { success: true };
  } catch (error) {
    console.error('Erreur deletePost:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Incrémenter les vues d'un article
 */
export const incrementViews = async (postId) => {
  try {
    const docRef = doc(db, COLLECTION, postId);
    await updateDoc(docRef, {
      views: increment(1)
    });
    return { success: true };
  } catch (error) {
    console.error('Erreur incrementViews:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Toggler le like d'un article
 * @param {string} postId - ID de l'article
 * @param {boolean} isLiking - true pour liker, false pour unliker
 */
export const toggleLike = async (postId, isLiking) => {
  try {
    const docRef = doc(db, COLLECTION, postId);
    await updateDoc(docRef, {
      // ✅ CORRIGÉ: Convertir le booléen en nombre (1 ou -1)
      likes: increment(isLiking ? 1 : -1)
    });
    return { success: true };
  } catch (error) {
    console.error('Erreur toggleLike:', error);
    return { success: false, error: error.message };
  }
};

export default {
  getAllPosts,
  getPostBySlug,
  getPostsByCategory,
  getRecentPosts,
  addPost,
  updatePost,
  deletePost,
  incrementViews,
  toggleLike
};